import React, { useEffect, useState } from "react";
import { Button, CircularProgress } from "@mui/material";
import PrimaryButton from "../../../../../components/Button/Button";
import {
  downloadPendingMailer,
  checkStatusAuditReport,
} from "../../../../../sevices/leadComplianceApi";
import toast from "react-hot-toast";
import useDownloader from "react-use-downloader";
import { useCustomSearchParams } from "../../../../../hooks/useCustomSearchParams";
import { useQueryClient } from "@tanstack/react-query";
import DownloadIcon from "../../../../../styles/svg/down-arrow-download.svg";

function MailerToSend({ btnTitle, downloadCallback, pdfName, downloadPath=false }) {
  const [loading, setLoading] = useState(false);
  const { size, elapsed, percentage, download, cancel, error, isInProgress } =
    useDownloader();
  const [building, setBuilding] = useCustomSearchParams("building");
  const [client, setClient] = useCustomSearchParams("client");
  const [year, setYear] = useCustomSearchParams("year");
  const [refetch, setRefetch] = useCustomSearchParams("refetch");

  // Generate unique localStorage key based on building, year, and pdfName
  const storageKey = `mailer-to-send-${building}-${year}-${pdfName}`;
  
  const [isGenerating, setIsGenerating] = useState(
    localStorage.getItem(storageKey) ? true : false
  );
  const [pdfId, setPdfId] = useState(localStorage.getItem(storageKey) || null);

  const path = downloadPath
    ? process.env.REACT_APP_BASE_URL
    : process.env.REACT_APP_BASE_URL + "/pdf/";

  // Polling logic
  useEffect(() => {
    let interval;

    if (pdfId && isGenerating) {
      interval = setInterval(async () => {
        checkStatusAuditReport(pdfId)
          .then((res) => {
            // Check both possible response structures
            const taskStatus = res.data.data?.task_status || res.data.status;
            const fileName = res.data.data?.file_name || res.data.file_name;
            const message = res.data.data?.message || res.data.message;

            if (taskStatus === "completed") {
              clearInterval(interval);
              const downloadUrl = downloadPath 
                ? `${process.env.REACT_APP_BASE_URL}${fileName}`
                : `${path}${fileName}`;
              download(downloadUrl, pdfName);
              setRefetch("refetch", refetch ? refetch + 1 : 1);
              setIsGenerating(false);
              setPdfId(null);
              localStorage.removeItem(storageKey);
            } else if (taskStatus === "failed") {
              toast.error(message || "PDF generation failed");
              clearInterval(interval);
              setIsGenerating(false);
              setPdfId(null);
              localStorage.removeItem(storageKey);
            }
          })
          .catch((err) => {
            console.log(err);
          });
      }, 6000);
    }

    return () => clearInterval(interval);
  }, [pdfId, isGenerating, path, downloadPath, pdfName, storageKey, refetch, setRefetch]);

  const downloadMailer = () => {
    setLoading(true);
    setIsGenerating(true);
    downloadCallback({
      building_id: building,
      year: year,
      download_and_send: true,
    })
      .then((res) => {
        // Check if response contains task_id (background processing)
        if (res.data.task_id) {
          setPdfId(res.data.task_id);
          localStorage.setItem(storageKey, res.data.task_id);
          toast.success(res.data.message || "PDF generation started");
          setLoading(false);
        } else {
          // Immediate download (no background processing)
          setRefetch("refetch", refetch ? refetch + 1 : 1);
          setLoading(false);
          setIsGenerating(false);
          download(
            `${path}${downloadPath ? res.data.pdf_file_path : res.data}`,
            pdfName
          );
        }
      })
      .catch((err) => {
        console.log(err);
        toast.error(err.response?.data?.message || "Failed to start download");
        setLoading(false);
        setIsGenerating(false);
      });
  };

  // Handle download errors
  useEffect(() => {
    if (error) {
      toast.error("Download failed. Please try again.");
    }
  }, [error]);

  const isDownloading = isGenerating || isInProgress;
  const buttonDisabled = isDownloading || !(building && client);
  
  const buttonText = isInProgress 
    ? (percentage > 0 ? `Downloading ${Math.round(percentage)}%` : "Starting Download...")
    : isGenerating 
    ? "Preparing..." 
    : btnTitle;

  return (
    <div className="mr-2">
      {" "}
      <PrimaryButton
        variant="contained"
        className={`capitalize blue-clr min-208`}
        onClick={downloadMailer}
        disabled={buttonDisabled}
        startIcon={
          isDownloading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <img src={DownloadIcon} />
          )
        }
      >
        {buttonText}
      </PrimaryButton>
    </div>
  );
}

export default MailerToSend;
